import { ReactNode } from "react";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "./legalLinks";

// ACA-266: the page shell every screen of the rebuilt wizard sits in — lockup, step tracker,
// content, footer. It is not OnboardingStage: that one is a 620px card centred on a grey field,
// and the design here is a left-aligned full-width page (max-width 1040px, white). Styles live in
// source/stylus/onboarding/includes/components/onboarding-v2.styl, behind the brand gate.

/** The design's tracker: four steps, and step 2 is "Practice", not "Goals" (AC8 says three). */
export const ONBOARDING_V2_STEP_LABELS: readonly string[] = [
  "You",
  "Practice",
  "Account",
  "Get started",
];

// Second copy of the lockup mkt already serves on the login screen (ACA-251); there is no shared
// asset route between the two.
export const ONBOARDING_V2_LOGO_SRC = "/assets/img/logos/logo-connect.svg";

export interface OnboardingV2Step {
  label: string;
  /** Supplied only for a step the flow can return to; without it the step is plain text. */
  onSelect?: () => void;
}

export interface OnboardingV2StageProps {
  steps: readonly OnboardingV2Step[];
  /** 1-based index of the step being shown (1 = the first step). */
  activeStep: number;
  /** ACA-344: the lockup links to /login on the steps before the account exists. */
  logoLinksToLogIn?: boolean;
  children: ReactNode;
}

export function OnboardingV2Stage({
  steps,
  activeStep,
  logoLinksToLogIn = false,
  children,
}: OnboardingV2StageProps) {
  const activeIndex = activeStep - 1;
  const progress = `${Math.round((activeStep / steps.length) * 100)}%`;
  // The design writes the year into the markup. ACA-251 AC4 requires it to move on its own.
  const year = new Date().getFullYear();
  const logo = (
    <img
      className="onboarding-v2__logo"
      src={ONBOARDING_V2_LOGO_SRC}
      alt="Artlogic Connect"
      data-testid="onboarding-v2-logo"
    />
  );

  return (
    <div className="onboarding-v2" data-testid="onboarding-v2-stage">
      <div className="onboarding-v2__main">
        {logoLinksToLogIn ? (
          <a
            aria-label="Artlogic Connect, log in"
            className="onboarding-v2__logo-link"
            data-testid="onboarding-v2-logo-link"
            href="/login"
          >
            {logo}
          </a>
        ) : (
          logo
        )}

        <div className="onboarding-v2__steps">
          <ol className="onboarding-v2__step-list" aria-label="Onboarding progress">
            {steps.map((step, index) => {
              const state =
                index < activeIndex
                  ? "done"
                  : index === activeIndex
                    ? "current"
                    : "upcoming";
              // The number and the tick are decoration: the list conveys the order and the label
              // carries the meaning.
              const dot = (
                <span className="onboarding-v2__step-dot" aria-hidden="true">
                  {state === "done" ? "✓" : index + 1}
                </span>
              );
              const label = (
                <span className="onboarding-v2__step-label">{step.label}</span>
              );

              return (
                <li
                  key={step.label}
                  className="onboarding-v2__step"
                  data-state={state}
                  data-testid={`onboarding-v2-step-${index + 1}`}
                  aria-current={state === "current" ? "step" : undefined}
                >
                  {step.onSelect ? (
                    <button
                      type="button"
                      className="onboarding-v2__step-button"
                      onClick={step.onSelect}
                    >
                      {dot}
                      {label}
                    </button>
                  ) : (
                    <>
                      {dot}
                      {label}
                    </>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="onboarding-v2__track" aria-hidden="true">
            <div
              className="onboarding-v2__progress"
              style={{ width: progress }}
              data-testid="onboarding-v2-progress"
            />
          </div>
        </div>

        {/* Keyed on the step so the entrance animation replays on every move, the way the
            design's screens do by swapping elements. */}
        <div className="onboarding-v2__step-content" key={activeStep}>
          {children}
        </div>

        <p className="onboarding-v2__footer" data-testid="onboarding-v2-footer">
          <span>&copy;{year} Artlogic Connect</span>
          <span className="onboarding-v2__footer-links">
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="onboarding-v2-footer-privacy"
            >
              Privacy Policy
            </a>
            <a
              href={TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="onboarding-v2-footer-terms"
            >
              Terms of Service
            </a>
          </span>
        </p>
      </div>
    </div>
  );
}
